/**
 * §C.9 Pattern A + Pattern D — open/close + tag filter + active
 * harness + task chain state for the `HarnessesSidePanel`.
 *
 * Pattern A (shipped 2026-04-29) put the open/close + tagFilter +
 * widgetLabel here so widget-embedded triggers
 * (`WidgetHarnessActionButton`) could pre-filter the panel without
 * prop-drilling through the workspace layout.
 *
 * Pattern D (in flight) lifts task-chain state out of
 * `HarnessesSidePanel`'s local `useState` so widgets can mount the
 * `AwaitingUserForm` inline when "their" task hits an awaiting-user
 * terminal — without re-subscribing to the same task. The single
 * `useTaskFrames` subscription still lives in `HarnessesSidePanel`;
 * it pushes terminal results into the store, and widgets read.
 *
 * Open call shapes:
 *   - `openClean()` — open with no filter (the CaseHeaderBar "Nova
 *     Tarefa" button + future Cmd+K palette entries).
 *   - `openWithFilter(tags, widgetLabel)` — open pre-filtered to the
 *     widget's domain. `widgetLabel` is the bridge that lets the
 *     widget identify "its" task in Pattern D.
 *   - `close()` — close + clear filter + clear chain so re-open
 *     starts clean.
 *
 * Filter semantics: a harness matches when ANY of its tags is in
 * the filter (OR-logic). Empty `tags` collapses to no filter.
 */

import { create } from "zustand";
import type {
  AwaitingUserQuestion,
  HarnessManifestSummary,
} from "@/types/harn";

/**
 * Pattern D — when a chain reaches an awaiting-user terminal, the
 * panel publishes this snapshot so the widget that triggered the
 * chain can render the form inline.
 */
export interface AwaitingUserContext {
  /** Widget label that triggered the chain (matches `widgetLabel` set on open). */
  widgetLabel: string;
  /** Harness id of the chain — used by widget-side resume POSTs. */
  harnessId: string;
  /** Active task id — the one currently in awaiting_user. */
  taskId: string;
  /** Resume token from the awaiting_user terminal. */
  resumeToken: string;
  /** Questions to render. Single source of truth for both the panel form and the widget-inline form. */
  questions: AwaitingUserQuestion[];
}

/**
 * Pattern C — published while a widget-triggered task is running
 * (or queued). Widgets observe this to mount a live progress strip
 * in the metadata slot. Cleared when the task reaches a terminal
 * (success / error / awaiting_user) — Pattern D and B take over.
 */
export interface CurrentProgress {
  widgetLabel: string;
  harnessId: string;
  taskId: string;
  /** "queued" | "running" — terminals are NOT published here. */
  status: "queued" | "running";
  /** Latest progress-frame step name (e.g. "extracting"); null if no progress frame yet. */
  step: string | null;
  /** Latest pct (0-100); null if not advertised. */
  pct: number | null;
  /** Latest progress message; null if absent. */
  message: string | null;
}

interface HarnessesPanelState {
  // ── Open / filter (Pattern A) ────────────────────────────────────
  isOpen: boolean;
  tagFilter: string[] | null;
  widgetLabel: string | null;

  // ── Active harness + chain (lifted in C9-D.1) ────────────────────
  /** Currently-selected harness from the catalogue (null until a tile is clicked). */
  selected: HarnessManifestSummary | null;
  /** Task chain: one entry per POST (initial run + each resume). Rightmost = active. */
  taskChain: string[];
  /** Most-recent run/resume error message; null when no error. */
  runError: string | null;
  /** True while a `runHarness` POST is in flight. */
  submitting: boolean;
  /** True while a `resumeHarness` POST is in flight. */
  resuming: boolean;
  /** Pattern D — populated when the active task hits awaiting_user. */
  awaitingUserContext: AwaitingUserContext | null;
  /** Pattern C — populated while a widget-triggered task is queued/running. */
  currentProgress: CurrentProgress | null;

  // ── Open / close actions (Pattern A) ─────────────────────────────
  openClean: () => void;
  /**
   * Open the panel with a tag filter. **Note:** an empty `tags` array
   * collapses to a clean open (no filter), since "no domain affinity"
   * is semantically equivalent to "no filter" — see
   * `harnessMatchesTagFilter` in `useHarnessCatalogue.ts`. Pass at
   * least one tag if you want a real filter; otherwise prefer
   * `openClean()` for clarity at the call site.
   */
  openWithFilter: (tags: string[], widgetLabel?: string) => void;
  /**
   * Clear `tagFilter` while preserving `widgetLabel` + chain state.
   * Used by the "ver tudo" affordance: the user opened the panel
   * from a specific widget but wants to see every reachable harness.
   * Pattern D still ties any task they start to the widget label.
   */
  clearFilter: () => void;
  close: () => void;

  // ── Active-harness + chain actions (C9-D.1) ──────────────────────
  setSelected: (h: HarnessManifestSummary | null) => void;
  setTaskChain: (chain: string[]) => void;
  appendTask: (taskId: string) => void;
  setRunError: (msg: string | null) => void;
  setSubmitting: (busy: boolean) => void;
  setResuming: (busy: boolean) => void;
  setAwaitingUserContext: (ctx: AwaitingUserContext | null) => void;
  setCurrentProgress: (progress: CurrentProgress | null) => void;
}

const CHAIN_INITIAL = {
  selected: null as HarnessManifestSummary | null,
  taskChain: [] as string[],
  runError: null as string | null,
  submitting: false,
  resuming: false,
  awaitingUserContext: null as AwaitingUserContext | null,
  currentProgress: null as CurrentProgress | null,
};

export const useHarnessesPanelStore = create<HarnessesPanelState>((set) => ({
  isOpen: false,
  tagFilter: null,
  widgetLabel: null,
  ...CHAIN_INITIAL,

  openClean: () =>
    set({
      isOpen: true,
      tagFilter: null,
      widgetLabel: null,
      ...CHAIN_INITIAL,
    }),
  openWithFilter: (tags, widgetLabel) =>
    set({
      isOpen: true,
      tagFilter: tags.length > 0 ? tags : null,
      widgetLabel: widgetLabel ?? null,
      ...CHAIN_INITIAL,
    }),
  clearFilter: () => set({ tagFilter: null }),
  close: () =>
    set({
      isOpen: false,
      tagFilter: null,
      widgetLabel: null,
      ...CHAIN_INITIAL,
    }),

  setSelected: (selected) =>
    set({
      selected,
      // Selecting a different harness resets the chain so the user
      // doesn't see stale results from the prior pick. Clear
      // `currentProgress` too — when the user picks a new harness
      // mid-run, `taskChain` becomes [] which unmounts
      // `HarnessTaskPanel`, so its publication effect can't fire its
      // own clear; without this set, the prior chain's in-progress
      // strip would persist on the originating widget.
      taskChain: [],
      runError: null,
      awaitingUserContext: null,
      currentProgress: null,
    }),
  setTaskChain: (taskChain) => set({ taskChain }),
  appendTask: (taskId) =>
    set((s) => ({ taskChain: [...s.taskChain, taskId] })),
  setRunError: (runError) => set({ runError }),
  setSubmitting: (submitting) => set({ submitting }),
  setResuming: (resuming) => set({ resuming }),
  setAwaitingUserContext: (awaitingUserContext) => set({ awaitingUserContext }),
  setCurrentProgress: (currentProgress) => set({ currentProgress }),
}));

/**
 * Pattern C selector helper — returns `currentProgress` only when
 * the widget label matches. Pair with `WidgetInProgressBadge` /
 * `WidgetHarnessStatus` to render a live progress strip in the
 * widget metadata slot.
 */
export function selectInProgressForWidget(
  state: HarnessesPanelState,
  widgetLabel: string,
): CurrentProgress | null {
  const p = state.currentProgress;
  if (!p) return null;
  return p.widgetLabel === widgetLabel ? p : null;
}

// DEV-only: Playwright specs introspect the panel store to verify
// publication of `currentProgress` / `awaitingUserContext` without
// depending on (a) timing-sensitive DOM mounts or (b) full E2E
// chain completion. Stripped in production by Vite DCE when DEV is
// false. Mirrors the `__cwsStore` pattern in useCaseWorkspaceStore.
if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__hpStore", {
    value: useHarnessesPanelStore,
    writable: false,
    enumerable: false,
    configurable: true,
  });
}

/**
 * Selector helper for widgets. Returns the `AwaitingUserContext` only
 * when the widget label matches the active chain — i.e., this widget
 * is the one whose task is awaiting input. Used by Pattern D-mounted
 * inline forms.
 */
export function selectAwaitingForWidget(
  state: HarnessesPanelState,
  widgetLabel: string,
): AwaitingUserContext | null {
  const ctx = state.awaitingUserContext;
  if (!ctx) return null;
  return ctx.widgetLabel === widgetLabel ? ctx : null;
}
